import React from 'react';
import {
  CreatePaymentMethodData,
  PaymentMethod,
  PaymentMethodCreateParams,
  StripeCardElement,
} from '@stripe/stripe-js';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { SubmitError } from '$/components/ui/Form';
import { useVerifyCardMutation } from '$/GraphQL/schema/schema';

type CreatePaymentMethod = (
  billingDetails: PaymentMethodCreateParams.BillingDetails,
  skipSetupIntendForIndiaCard?: boolean
) => Promise<PaymentMethod>;

export const useCreatePaymentMethod = (): CreatePaymentMethod => {
  const stripe = useStripe();
  const elements = useElements();
  const [verifyCard] = useVerifyCardMutation();

  const createPaymentMethod: CreatePaymentMethod = React.useCallback(
    async (billingDetails) => {
      if (!stripe || !elements) {
        console.error('Stripe elements not loaded.');
        throw 'Error registering.  Please retry in a few seconds.';
      }

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        console.error('Card element not loaded');
        throw 'Error registering.  Please retry in a few seconds.';
      }

      const paymentMethodData: CreatePaymentMethodData = {
        type: 'card',
        card: cardElement as StripeCardElement,
        billing_details: billingDetails,
      };

      const { error, paymentMethod } = await stripe.createPaymentMethod(paymentMethodData);
      if (error || !paymentMethod) {
        if (error?.code === 'card_declined' && error?.decline_code === 'transaction_not_allowed') {
          throw new SubmitError({
            card: 'Your credit card issuer has rejected a pre-authorization required for sign up. Please contact your issuer or bank to whitelist "eWebinar" as a new vendor. If you continue to have troubles signing up, please contact support@ewebinar.com.',
          });
        } else {
          throw new SubmitError({
            card: error?.message ?? 'Error submitting credit card',
          });
        }
      }
      return paymentMethod;
    },
    [elements, stripe]
  );

  const createPaymentMethodAndVerify: CreatePaymentMethod = React.useCallback(
    async (billingDetails) => {
      if (!stripe || !elements) {
        console.error('Stripe elements not loaded.');
        throw 'Error registering.  Please retry in a few seconds.';
      }
      let paymentMethod = await createPaymentMethod(billingDetails);
      // try to charge user $1 then refund it to verify card
      const { data, errors } = await verifyCard({
        variables: {
          paymentMethodId: paymentMethod.id,
        },
      });

      const verifyFailed =
        errors || (!data?.verifyCard.success && !data?.verifyCard.paymentIntentSecret);
      if (verifyFailed) {
        throw new SubmitError({
          card: 'Your credit card issuer has rejected a pre-authorization required for sign up. Please contact your issuer or bank to whitelist "eWebinar" as a new vendor. If you continue to have troubles signing up, please contact support@ewebinar.com.',
        });
      }

      if (data?.verifyCard.paymentIntentSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          data.verifyCard.paymentIntentSecret
        );

        if (confirmError) {
          throw new SubmitError({
            card: confirmError?.message ?? 'Error submitting credit card',
          });
        }
      }

      // recreate payment method - the old one is no longer valid because charges without attaching to the customer
      paymentMethod = await createPaymentMethod(billingDetails);

      return paymentMethod;
    },
    [createPaymentMethod, elements, stripe, verifyCard]
  );

  const disableChargeToVerify = true;

  return disableChargeToVerify ? createPaymentMethod : createPaymentMethodAndVerify;
};
